import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { adminClient, type AdminRunSegment, type AdminSessionRunsResponse } from '@/api/adminClient';
import { RunFeedbackSummary } from '@/components/runs/RunFeedbackSummary';
import { StatusPill } from '@/components/runs/StatusPill';
import { WaitBar } from '@/components/runs/WaitBreakdown';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import { AdminBreadcrumbs } from '@/components/shell/AdminBreadcrumbs';
import { PAGE_CONTAINER_CLASS, PAGE_TITLE_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminPageLoadError } from '@/components/shell/AdminPageLoadError';
import { AdminSection } from '@/components/shell/AdminSection';
import { formatDateTime, formatUsd } from '@/lib/format';
import { orgBasePath, orgMoneyPath, orgSectionPath } from '@/lib/orgNav';
import {
  SEGMENT_META,
  engineLabel,
  formatDurationMs,
  humanizeKey,
  markerOffset,
  runEngineLabel,
  runStatusLabel,
  runStatusTone,
} from '@/lib/runTelemetry';
import { cn } from '@/lib/utils';
import { useOrgScope } from '@/pages/org/OrgLayout';

type EngineFilter = 'all' | 'opencode' | 'omp';

function Latency({ ms, warnAtMs }: { ms: number | undefined; warnAtMs: number }) {
  if (ms === undefined) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={cn('tabular-nums', ms >= warnAtMs ? 'font-medium text-warning' : 'text-foreground')}>
      {formatDurationMs(ms)}
    </span>
  );
}

export function SessionDetailPage() {
  const { sessionId = '' } = useParams();
  const { orgId } = useOrgScope();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminSessionRunsResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);
  const [engine, setEngine] = useState<EngineFilter>('all');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const next = await adminClient.getSessionRuns(orgId, sessionId, engine === 'all' ? undefined : engine);
        if (cancelled) return;
        setData(next);
        setError(undefined);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load session');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, sessionId, engine, reloadKey]);

  const orgPath = orgBasePath(orgId);

  // A filter change keeps the page up while the narrower list loads.
  if (loading && !data) {
    return (
      <div className={PAGE_CONTAINER_CLASS}>
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading session runs
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <AdminPageLoadError
        message={error}
        onRetry={() => setReloadKey((key) => key + 1)}
        backTo={orgMoneyPath(orgId, 'spend')}
        backLabel="Back to spend"
      />
    );
  }

  if (!data) {
    return (
      <div className={PAGE_CONTAINER_CLASS}>
        <div className="space-y-3">
          <p className="text-sm text-destructive">Session not found</p>
          <Link to={orgMoneyPath(orgId, 'spend')} className="text-sm text-primary hover:underline">
            Back to spend
          </Link>
        </div>
      </div>
    );
  }

  const { session, project, runs } = data;
  const projectLabel = project?.displayName || project?.title || session.projectId;

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminBreadcrumbs
        items={[
          { label: 'Projects', to: orgSectionPath(orgId, 'projects') },
          { label: projectLabel, to: `${orgPath}/projects/${encodeURIComponent(session.projectId)}` },
          { label: session.title },
        ]}
      />
      <header className="mb-6 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {session.status ? <StatusPill tone="neutral">{humanizeKey(session.status)}</StatusPill> : null}
          {session.deleted ? <StatusPill tone="danger">Deleted</StatusPill> : null}
        </div>
        <h1 className={PAGE_TITLE_CLASS}>{session.title}</h1>
        <p className="text-sm text-muted-foreground">
          {[
            project?.packageName,
            session.createdByName || session.createdByUserId,
            session.createdAt && `created ${formatDateTime(session.createdAt)}`,
            `${runs.length}${data.truncated ? '+' : ''} run${runs.length === 1 ? '' : 's'}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <p className="font-mono text-xs text-muted-foreground">{session.sessionId}</p>
      </header>

      <AdminSection
        title="Runs"
        description="Newest first. Open a run for its full wait breakdown, phase timeline and event log."
        bodyClassName="p-0"
        actions={
          <SegmentedFilter<EngineFilter>
            label="Coding agent"
            value={engine}
            onChange={setEngine}
            options={[
              { id: 'all', label: 'All' },
              { id: 'opencode', label: engineLabel('opencode') },
              { id: 'omp', label: engineLabel('omp') },
            ]}
          />
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-muted/40 text-xs font-medium text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Run</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Model</th>
                <th className="px-4 py-3 text-right">Seat claim</th>
                <th className="px-4 py-3 text-right">First output</th>
                <th className="px-4 py-3 text-right">First tool</th>
                <th className="min-w-[10rem] px-4 py-3">Build → first tool</th>
                <th className="px-4 py-3">Feedback</th>
                <th className="px-4 py-3 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                    {engine === 'all' ? 'No runs in this session yet' : `No runs on ${engineLabel(engine)} in this session`}
                  </td>
                </tr>
              ) : (
                runs.map((run) => {
                  const { telemetry } = run;
                  const runPath = `${orgPath}/runs/${encodeURIComponent(run.runId)}`;
                  const toFirstTool = markerOffset(telemetry, 'agent.first_tool');
                  const dominant = telemetry.waitBreakdown?.segments.reduce<AdminRunSegment | undefined>(
                    (best, segment) => (!best || segment.durationMs > best.durationMs ? segment : best),
                    undefined,
                  );
                  const kind = run.qualityRepair
                    ? 'Quality repair'
                    : run.mode && run.mode !== 'normal'
                      ? humanizeKey(run.mode)
                      : 'Build';
                  return (
                    <tr
                      key={run.runId}
                      onClick={() => navigate(runPath)}
                      className="cursor-pointer border-b border-border align-top last:border-0 hover:bg-muted/40"
                    >
                      <td className="max-w-sm px-4 py-3">
                        <Link
                          to={runPath}
                          onClick={(event) => event.stopPropagation()}
                          className="font-medium text-foreground hover:underline"
                        >
                          {formatDateTime(run.createdAt)}
                        </Link>
                        <p className="text-xs text-muted-foreground">
                          {kind}
                          {run.createdByName ? ` · ${run.createdByName}` : ''}
                        </p>
                        {run.promptSnippet ? (
                          <p className="mt-1 truncate text-xs text-muted-foreground">{run.promptSnippet}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill tone={runStatusTone(run.status)}>{runStatusLabel(run.status)}</StatusPill>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <p className="text-foreground">
                          {telemetry.model.resolved.join(', ') || telemetry.model.requested || '—'}
                        </p>
                        <p className="text-muted-foreground">{runEngineLabel(run.agentEngine, run.agentEngineFallback)}</p>
                        {telemetry.effort.requested ? (
                          <p className="text-muted-foreground">
                            effort {telemetry.effort.requested}
                            {telemetry.effort.effective && telemetry.effort.effective !== telemetry.effort.requested
                              ? ` → ${telemetry.effort.effective}`
                              : ''}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Latency ms={telemetry.seat.claimMs} warnAtMs={20_000} />
                        {telemetry.seat.queuedMs ? (
                          <p className="text-xs text-warning">queued {formatDurationMs(telemetry.seat.queuedMs)}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Latency ms={telemetry.ttft.ms} warnAtMs={20_000} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Latency ms={telemetry.firstTool.ms} warnAtMs={30_000} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-baseline justify-between gap-2 text-xs">
                          <Latency ms={toFirstTool} warnAtMs={45_000} />
                          {dominant ? (
                            <span className="truncate text-muted-foreground">
                              mostly {SEGMENT_META[dominant.key].label.toLowerCase()}
                            </span>
                          ) : null}
                        </div>
                        <WaitBar telemetry={telemetry} className="mt-1.5" />
                      </td>
                      <td className="px-4 py-3">
                        <RunFeedbackSummary feedback={run.feedback} compact emptyLabel="—" />
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {telemetry.usage.costUsd !== undefined ? formatUsd(telemetry.usage.costUsd) : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {data.truncated ? (
          <p className="border-t border-border px-4 py-3 text-xs text-muted-foreground">
            Showing the 50 most recent runs.
          </p>
        ) : null}
      </AdminSection>
    </div>
  );
}
